import { Box, styled } from "@mui/material";

export const ANDROID_SCREEN_WIDTH = 412;
export const ANDROID_SCREEN_HEIGHT = 915;
const BEZEL = 10;
export const ANDROID_PHONE_WIDTH = ANDROID_SCREEN_WIDTH + BEZEL * 2;
export const ANDROID_PHONE_HEIGHT = ANDROID_SCREEN_HEIGHT + BEZEL * 2;

export const ANDROID_FONT =
  '"Google Sans Text", "Google Sans", Roboto, "Helvetica Neue", Arial, sans-serif';

export const ANDROID = {
  ink: "#1B1C1F",
  secondary: "#5F6370",
  primary: "#0B57D0",
  surface: "#F0F2F8",
  container: "#FBFBFE",
  bubble: "#E5E9F3",
  field: "#E0E4EF",
  card: "#FFFFFF",
  outline: "#C4C7D0",
  avatar: "#C9D6EE",
  avatarInk: "#3A4A6B",
  bezel: "#101012",
  bezelEdge: "#3B3C40",
} as const;

export const Device = styled(Box)({
  width: ANDROID_PHONE_WIDTH,
  height: ANDROID_PHONE_HEIGHT,
  padding: BEZEL - 2,
  border: `2px solid ${ANDROID.bezelEdge}`,
  borderRadius: 54,
  backgroundColor: ANDROID.bezel,
  boxSizing: "border-box",
  fontFamily: ANDROID_FONT,
  color: ANDROID.ink,
  "& *": { boxSizing: "border-box" },
});

export const Screen = styled(Box)({
  position: "relative",
  width: ANDROID_SCREEN_WIDTH,
  height: ANDROID_SCREEN_HEIGHT,
  borderRadius: 44,
  overflow: "hidden",
  clipPath: "inset(0 round 44px)",
  backgroundColor: ANDROID.surface,
});

export const PunchHole = styled(Box)({
  position: "absolute",
  top: 17,
  left: "50%",
  transform: "translateX(-50%)",
  width: 22,
  height: 22,
  borderRadius: "50%",
  backgroundColor: "#000",
  boxShadow: "inset 0 0 0 3px #1B1B1F",
  zIndex: 5,
});

export const StatusBar = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: 48,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "0 28px 0 26px",
  fontSize: 14,
  fontWeight: 500,
  letterSpacing: "0.01em",
  zIndex: 4,
  "& > span": { display: "flex", gap: 6, alignItems: "center" },
  "& svg": { display: "block" },
});

export const NavPill = styled(Box)({
  position: "absolute",
  bottom: 10,
  left: "50%",
  transform: "translateX(-50%)",
  width: 108,
  height: 4,
  borderRadius: 2,
  backgroundColor: "currentColor",
});

export const AppBar = styled(Box)({
  position: "absolute",
  top: 48,
  left: 0,
  right: 0,
  height: 64,
  display: "flex",
  alignItems: "center",
  padding: "0 4px",
  gap: 4,
});

export const IconButton = styled(Box)({
  flex: "0 0 auto",
  width: 48,
  height: 48,
  display: "grid",
  placeItems: "center",
  color: ANDROID.ink,
});

export const AppBarIdentity = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  gap: 12,
  paddingLeft: 4,
});

export const Avatar = styled(Box)({
  flex: "0 0 auto",
  width: 40,
  height: 40,
  borderRadius: "50%",
  backgroundColor: ANDROID.avatar,
  color: ANDROID.avatarInk,
  display: "grid",
  placeItems: "center",
});

export const AppBarName = styled(Box)({
  fontSize: 16,
  lineHeight: "24px",
  fontWeight: 500,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

export const ThreadContainer = styled(Box)({
  position: "absolute",
  top: 112,
  left: 0,
  right: 0,
  bottom: 96,
  padding: "20px 16px 0",
  borderRadius: "28px 28px 0 0",
  backgroundColor: ANDROID.container,
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
});

export const ThreadStack = styled(Box)({
  marginTop: "auto",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 6,
  paddingBottom: 8,
});

export const Stamp = styled(Box)({
  alignSelf: "center",
  fontSize: 12,
  lineHeight: "16px",
  color: ANDROID.secondary,
  marginBottom: 10,
});

export const Bubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== "hint",
})<{ hint?: boolean }>(({ hint }) => ({
  maxWidth: 300,
  minHeight: 48,
  padding: "12px 16px",
  borderRadius: "20px 20px 20px 4px",
  backgroundColor: ANDROID.bubble,
  color: hint ? ANDROID.secondary : ANDROID.ink,
  fontSize: 16,
  lineHeight: "24px",
  whiteSpace: "pre-wrap",
  overflowWrap: "anywhere",
  "& .preview-url": { color: ANDROID.primary, textDecoration: "underline" },
}));

export const ImageBubble = styled(Box)({
  maxWidth: 300,
  borderRadius: "20px 20px 20px 4px",
  overflow: "hidden",
  "& img": { display: "block", width: "100%", height: "auto" },
});

export const RichCard = styled(Box)({
  maxWidth: 300,
  borderRadius: 20,
  overflow: "hidden",
  backgroundColor: ANDROID.card,
  border: `1px solid ${ANDROID.outline}`,
  "& img": { display: "block", width: "100%", height: "auto" },
});

export const RichCardText = styled(Box, {
  shouldForwardProp: (prop) => prop !== "hint",
})<{ hint?: boolean }>(({ hint }) => ({
  padding: "12px 16px",
  color: hint ? ANDROID.secondary : ANDROID.ink,
  fontSize: 16,
  lineHeight: "24px",
  whiteSpace: "pre-wrap",
  overflowWrap: "anywhere",
  "& .preview-url": { color: ANDROID.primary, textDecoration: "underline" },
}));

export const RichCardCta = styled(Box)({
  height: 48,
  padding: "0 16px",
  display: "flex",
  alignItems: "center",
  borderTop: `1px solid ${ANDROID.outline}`,
  color: ANDROID.primary,
  fontSize: 14,
  fontWeight: 500,
});

export const ChipRow = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
  paddingTop: 4,
});

export const Chip = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  height: 32,
  padding: "0 16px 0 12px",
  borderRadius: 16,
  border: `1px solid ${ANDROID.outline}`,
  color: ANDROID.primary,
  fontSize: 14,
  fontWeight: 500,
  whiteSpace: "nowrap",
  "& svg": { fontSize: 18 },
});

export const MessageTime = styled(Box)({
  paddingLeft: 16,
  fontSize: 12,
  lineHeight: "16px",
  color: ANDROID.secondary,
});

export const Composer = styled(Box)({
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  height: 96,
  padding: "8px 8px 0",
  display: "flex",
  alignItems: "flex-start",
  gap: 8,
  backgroundColor: ANDROID.surface,
});

export const ComposerPlus = styled(Box)({
  flex: "0 0 auto",
  width: 48,
  height: 48,
  borderRadius: "50%",
  backgroundColor: ANDROID.field,
  color: ANDROID.ink,
  display: "grid",
  placeItems: "center",
});

export const ComposerField = styled(Box)({
  flex: 1,
  minWidth: 0,
  height: 48,
  padding: "0 12px 0 14px",
  borderRadius: 24,
  backgroundColor: ANDROID.field,
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: 16,
  color: ANDROID.secondary,
  whiteSpace: "nowrap",
  "& > span": { flex: 1, overflow: "hidden", textOverflow: "ellipsis" },
});

export const ComposerMic = styled(Box)({
  flex: "0 0 auto",
  width: 48,
  height: 48,
  borderRadius: "50%",
  backgroundColor: ANDROID.primary,
  color: "#fff",
  display: "grid",
  placeItems: "center",
});

export const LockRoot = styled(Box)({
  position: "absolute",
  inset: 0,
  color: "#fff",
  background: [
    "radial-gradient(90% 60% at 80% 10%, #FFD8B5 0%, rgba(255,216,181,0) 60%)",
    "radial-gradient(90% 70% at 10% 40%, #9FD3C7 0%, rgba(159,211,199,0) 60%)",
    "radial-gradient(80% 60% at 60% 100%, #4B6CB7 0%, rgba(75,108,183,0) 70%)",
    "linear-gradient(180deg, #E7C8D8 0%, #8FA8D9 55%, #28325E 100%)",
  ].join(", "),
});

export const LockClock = styled(Box)({
  position: "absolute",
  left: 24,
  top: 62,
  fontSize: 86,
  lineHeight: 1,
  fontWeight: 500,
  letterSpacing: "-0.035em",
});

export const Smartspace = styled(Box)({
  position: "absolute",
  left: 26,
  top: 166,
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: 16,
});

export const NotificationStack = styled(Box)({
  position: "absolute",
  left: 16,
  right: 16,
  top: 236,
  display: "flex",
  flexDirection: "column",
  gap: 4,
});

export const ConversationCard = styled("button")({
  all: "unset",
  boxSizing: "border-box",
  position: "relative",
  display: "block",
  width: "100%",
  minHeight: 88,
  padding: "20px 16px 20px 80px",
  borderRadius: 28,
  backgroundColor: "#F1F3F9",
  color: ANDROID.ink,
  fontFamily: "inherit",
  textAlign: "left",
  cursor: "pointer",
  "&:focus-visible": { outline: `2px solid ${ANDROID.primary}` },
});

export const CardAvatar = styled(Box)({
  position: "absolute",
  left: 16,
  top: 20,
  width: 48,
  height: 48,
  borderRadius: "50%",
  backgroundColor: ANDROID.avatar,
  color: ANDROID.avatarInk,
  display: "grid",
  placeItems: "center",
});

export const CardBadge = styled(Box)({
  position: "absolute",
  right: -4,
  bottom: -4,
  width: 20,
  height: 20,
  borderRadius: "50%",
  backgroundColor: ANDROID.primary,
  border: "2px solid #F1F3F9",
  color: "#fff",
  display: "grid",
  placeItems: "center",
});

export const CardHead = styled(Box)({
  display: "flex",
  alignItems: "baseline",
  gap: 4,
  lineHeight: "20px",
  paddingRight: 44,
  minWidth: 0,
  "& > b": {
    flex: "0 1 auto",
    minWidth: 0,
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  "& > span": {
    flex: "0 0 auto",
    fontSize: 12,
    color: ANDROID.secondary,
    whiteSpace: "nowrap",
  },
});

export const CardBody = styled(Box, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded: boolean }>(({ expanded }) => ({
  fontSize: 14,
  lineHeight: "20px",
  whiteSpace: "pre-wrap",
  overflowWrap: "anywhere",
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: expanded ? 7 : 1,
  overflow: "hidden",
}));

export const CardImage = styled(Box)({
  marginTop: 8,
  borderRadius: 12,
  overflow: "hidden",
  maxHeight: 200,
  "& img": {
    display: "block",
    width: "100%",
    height: "auto",
    maxHeight: 200,
    objectFit: "cover",
  },
});

export const CardActions = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 24,
  height: 60,
  margin: "8px 0 -20px -64px",
  paddingLeft: 20,
  fontSize: 14,
  fontWeight: 500,
  color: ANDROID.primary,
});

export const ExpandPill = styled(Box, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded: boolean }>(({ expanded }) => ({
  position: "absolute",
  right: 16,
  top: 20,
  height: 24,
  minWidth: 36,
  borderRadius: 12,
  backgroundColor: "rgba(27, 28, 31, 0.08)",
  display: "grid",
  placeItems: "center",
  "& svg": {
    transform: expanded ? "rotate(180deg)" : "none",
    transition: "transform 150ms ease-in-out",
  },
}));

export const LockShortcut = styled(Box, {
  shouldForwardProp: (prop) => prop !== "side",
})<{ side: "left" | "right" }>(({ side }) => ({
  position: "absolute",
  bottom: 64,
  [side]: 36,
  width: 48,
  height: 48,
  borderRadius: "50%",
  backgroundColor: "rgba(255, 255, 255, 0.22)",
  display: "grid",
  placeItems: "center",
}));

export const LockGlyphSlot = styled(Box)({
  position: "absolute",
  left: "50%",
  bottom: 88,
  transform: "translateX(-50%)",
});
